import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { submitPrivacyConsent } from '../../utils/apis/user-service';
import { useAuthStore } from '../../utils/stores/useAuthStore';
import { useUserStore } from '../../utils/stores/useUserStore';

const POLICY_URL = process.env.REACT_APP_PRIVACY_POLICY_URL || '/assets/new-platform/v2.0_privacy_policy.pdf';
const CURRENT_VERSION = process.env.REACT_APP_PRIVACY_POLICY_CURRENT_VERSION || 'v2.0';

export const PrivacyPolicyReacceptanceModal: React.FC = () => {
    const navigate = useNavigate();
    const { auth, logout } = useAuthStore();
    const { updateUserData } = useUserStore();
    const [acknowledged, setAcknowledged] = useState(false);
    const [d1Consent, setD1Consent] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const onSubmit = async () => {
        if (!acknowledged || !auth.userId || !auth.idToken || submitting) return;
        setSubmitting(true);
        setError(null);
        try {
            const updated = await submitPrivacyConsent(auth.userId, auth.idToken, CURRENT_VERSION, d1Consent);
            updateUserData({
                acceptedPrivacyVersion: updated.accepted_privacy_version,
                acceptedPrivacyAt: updated.accepted_privacy_at,
                d1Consent: updated.d1_consent,
                d1ConsentAt: updated.d1_consent_at,
            });
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Errore imprevisto');
            setSubmitting(false);
        }
    };

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="pp-title"
            className="fixed inset-0 bg-black/70 flex justify-center items-center z-[9999]"
        >
            <div className="bg-white p-8 rounded-lg w-[90%] max-w-[560px] max-h-[90vh] overflow-y-auto">
                <h2 id="pp-title" className="text-[#333] text-xl font-bold mb-4">
                    Aggiornamento Informativa Privacy
                </h2>
                <p className="text-[#666] leading-snug mb-5">
                    Abbiamo aggiornato l'informativa sulla privacy. Per continuare ad utilizzare la piattaforma,
                    leggi il documento aggiornato e conferma di averne preso visione.
                </p>

                <label className="flex items-start gap-3 mb-4 cursor-pointer text-[#333] leading-snug">
                    <input
                        type="checkbox"
                        checked={acknowledged}
                        onChange={(e) => setAcknowledged(e.target.checked)}
                        className="mt-1 shrink-0"
                    />
                    <span>
                        Ho letto e preso visione dell'informativa aggiornata
                        <a
                            href={POLICY_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ml-1 text-[#2057f5] underline"
                        >
                            (apri il documento)
                        </a>
                    </span>
                </label>

                <label className="flex items-start gap-3 mb-4 cursor-pointer text-[#333] leading-snug">
                    <input
                        type="checkbox"
                        checked={d1Consent}
                        onChange={(e) => setD1Consent(e.target.checked)}
                        className="mt-1 shrink-0"
                    />
                    <span>
                        Consento l'utilizzo dei miei dati clinici per finalità di cui alla lettera D.1 dell'informativa,
                        ossia per analisi statistiche, ricerca interna e miglioramento dei servizi, con produzione di risultati
                        esclusivamente aggregati e anonimi. Per maggiori informazioni su come Holifya tratta i tuoi dati,
                        visita l'informativa completa
                        <a
                            href={POLICY_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ml-1 text-[#2057f5] underline"
                        >
                            qui
                        </a>.
                    </span>
                </label>

                {error && <p className="text-[#c00] text-sm mb-3">{error}</p>}

                <div className="flex flex-col gap-2 mt-6">
                    <button
                        onClick={onSubmit}
                        disabled={!acknowledged || submitting}
                        className="bg-[#2057f5] text-white font-semibold rounded-lg py-3 px-4 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {submitting ? 'Salvataggio in corso...' : 'Continua'}
                    </button>
                    <button
                        onClick={() => logout(navigate)}
                        className="bg-transparent text-[#666] border border-[#ddd] rounded-lg py-2 px-4 cursor-pointer"
                    >
                        Esci
                    </button>
                </div>
            </div>
        </div>
    );
};
